/**
 * Scene System V2 - Scene Action Engine
 *
 * Processes scene actions using device capabilities from device configurations.
 * Independent engine for Flow-based scene action execution.
 */

import type {
	SceneAction,
	SceneExecutionContext,
	LegacySceneAction,
	ParameterValue,
	CommandTemplate,
} from '../types/scene-capabilities';
import { deviceSceneCapabilities } from './DeviceSceneCapabilities';
import { is_gen_2 } from '../../../helpers/interface_helpers';
import { gen1CloudReq } from '../../../services/gen_1_reqs';
import type { Device } from '../../../types/Device';

export interface ActionExecutionResult {
	success: boolean;
	command?: any;
	error?: string;
	warnings?: string[];
	metadata?: Record<string, any>;
}

export interface ActionExecutionOptions {
	validateOnly?: boolean;
	dryRun?: boolean;
	timeout?: number;
	retryCount?: number;
}

export class SceneActionEngine {
	private static instance: SceneActionEngine;

	private constructor() {}

	public static getInstance(): SceneActionEngine {
		if (!SceneActionEngine.instance) {
			SceneActionEngine.instance = new SceneActionEngine();
		}
		return SceneActionEngine.instance;
	}

	/**
	 * Execute a scene action using device capabilities
	 */
	public async executeAction(
		context: SceneExecutionContext,
		actionName: string,
		parameters: Record<string, ParameterValue>,
		options: ActionExecutionOptions = {},
	): Promise<ActionExecutionResult> {
		try {
			// Get action definition from device capabilities
			const actionDef = context.capabilities.actions[actionName];

			if (!actionDef) {
				return {
					success: false,
					error: `Action '${actionName}' not supported by device type '${context.deviceType}'`,
				};
			}

			// Validate parameters
			const validationResult = this.validateActionParameters(actionDef, parameters);
			if (!validationResult.success) {
				return {
					success: false,
					error: validationResult.error,
					warnings: validationResult.warnings,
				};
			}

			// Return early if validation only
			if (options.validateOnly) {
				return {
					success: true,
					metadata: { validated: true },
				};
			}

			// Generate command from template
			const command = this.generateActionCommand(context, actionDef, parameters);

			// Return command for dry run
			if (options.dryRun) {
				return {
					success: true,
					command,
					metadata: { dryRun: true },
				};
			}

			// Execute the actual command
			return await this.executeActionCommand(context, command, options);
		} catch (error) {
			return {
				success: false,
				error: `Action execution failed: ${error.message}`,
			};
		}
	}

	/**
	 * Execute multiple actions in sequence
	 */
	public async executeActions(
		context: SceneExecutionContext,
		actions: Array<{ name: string; parameters: Record<string, ParameterValue> }>,
		options: ActionExecutionOptions = {},
	): Promise<ActionExecutionResult[]> {
		const results: ActionExecutionResult[] = [];

		for (const action of actions) {
			const result = await this.executeAction(context, action.name, action.parameters, options);
			results.push(result);

			// Stop on first failure unless continuing is specified
			if (!result.success && !options.dryRun) {
				break;
			}
		}

		return results;
	}

	/**
	 * Convert legacy scene action to V2 format and execute
	 */
	public async executeLegacyAction(
		context: SceneExecutionContext,
		legacyAction: LegacySceneAction,
		options: ActionExecutionOptions = {},
	): Promise<ActionExecutionResult> {
		try {
			// Convert legacy action to V2 format
			const v2Action = this.convertLegacyAction(legacyAction);

			if (!v2Action) {
				return {
					success: false,
					error: `Unable to convert legacy action: ${legacyAction.name}`,
				};
			}

			// Execute using V2 engine
			return await this.executeAction(context, v2Action.name, v2Action.parameters, options);
		} catch (error) {
			return {
				success: false,
				error: `Legacy action execution failed: ${error.message}`,
			};
		}
	}

	/**
	 * Get available actions for a device type
	 */
	public getAvailableActions(deviceType: string): string[] {
		const capabilities = deviceSceneCapabilities.getCapabilities(deviceType);

		if (!capabilities) {
			return [];
		}

		return Object.keys(capabilities.actions);
	}

	/**
	 * Get action definition for a device type and action
	 */
	public getActionDefinition(deviceType: string, actionName: string): SceneAction | null {
		const capabilities = deviceSceneCapabilities.getCapabilities(deviceType);

		if (!capabilities) {
			return null;
		}

		return capabilities.actions[actionName] || null;
	}

	/**
	 * Validate action parameters against action definition
	 */
	public validateActionParameters(
		action: SceneAction,
		parameters: Record<string, ParameterValue>,
	): { success: boolean; error?: string; warnings?: string[] } {
		const warnings: string[] = [];

		// Check required parameters
		for (const [paramName, paramDef] of Object.entries(action.parameters)) {
			if (paramDef.required && !(paramName in parameters)) {
				return {
					success: false,
					error: `Required parameter '${paramName}' is missing`,
				};
			}
		}

		// Validate parameter types and constraints
		for (const [paramName, value] of Object.entries(parameters)) {
			const paramDef = action.parameters[paramName];

			if (!paramDef) {
				warnings.push(`Unknown parameter '${paramName}' will be ignored`);
				continue;
			}

			const typeValidation = this.validateParameterType(paramDef.type, value);
			if (!typeValidation.success) {
				return {
					success: false,
					error: `Parameter '${paramName}': ${typeValidation.error}`,
				};
			}

			const constraintValidation = this.validateParameterConstraints(paramDef, value);
			if (!constraintValidation.success) {
				return {
					success: false,
					error: `Parameter '${paramName}': ${constraintValidation.error}`,
				};
			}
		}

		// Validate action-level validation rules
		for (const rule of action.validation) {
			const ruleValidation = this.validateRule(rule, parameters);
			if (!ruleValidation.success) {
				if (rule.severity === 'error') {
					return {
						success: false,
						error: rule.errorMessage,
					};
				} else {
					warnings.push(rule.errorMessage);
				}
			}
		}

		return { success: true, warnings };
	}

	// Private methods

	private generateActionCommand(
		context: SceneExecutionContext,
		action: SceneAction,
		parameters: Record<string, ParameterValue>,
	): any {
		const template = action.commandTemplate;
		const command = JSON.parse(JSON.stringify(template)); // Deep clone

		// Replace placeholders in command template
		const replacedPayload = this.replacePlaceholders(command.payload, {
			...parameters,
			deviceId: context.deviceId,
			channel: context.channel,
			timestamp: context.timestamp,
		});

		return {
			...command,
			payload: replacedPayload,
			metadata: {
				deviceId: context.deviceId,
				deviceType: context.deviceType,
				actionName: action.name,
				executionId: context.executionId,
			},
		};
	}

	private async executeActionCommand(
		context: SceneExecutionContext,
		command: any,
		options: ActionExecutionOptions,
	): Promise<ActionExecutionResult> {
		try {
			console.log('SceneActionEngine: Executing command:', command);

			// Get device from context (needs to be passed in execution context)
			const device = context.device as Device;

			if (!device) {
				return {
					success: false,
					error: 'Device not found in execution context',
					command,
				};
			}

			// Detect device generation
			const isGen2 = is_gen_2(device);

			let result;

			if (isGen2) {
				// Gen2+ RPC execution
				result = await this.executeGen2Command(device, context, command, options);
			} else {
				// Gen1 HTTP execution
				result = await this.executeGen1Command(device, context, command, options);
			}

			return {
				success: result.success,
				command,
				metadata: {
					executedAt: Date.now(),
					deviceId: context.deviceId,
					generation: isGen2 ? 2 : 1,
					...result.metadata,
				},
			};
		} catch (error) {
			return {
				success: false,
				error: `Command execution failed: ${error.message}`,
				command,
			};
		}
	}

	/**
	 * Execute Gen1 command via HTTP endpoint
	 */
	private async executeGen1Command(
		device: Device,
		context: SceneExecutionContext,
		command: any,
		options: ActionExecutionOptions,
	): Promise<{ success: boolean; metadata?: any }> {
		try {
			// Parse Gen1 'set' parameter from endpoint
			// '/device/relay/control' → 'relay'
			// '/device/light/control' → 'light'
			// '/device/roller/control' → 'roller'
			const set = this.parseGen1SetFromEndpoint(command.endpoint);

			// Execute using gen1CloudReq
			const timeout = options.timeout || 6000;
			const res = await gen1CloudReq(device, set, command.params, timeout);

			return {
				success: res.isok === true,
				metadata: {
					response: res,
					endpoint: command.endpoint,
					set,
				},
			};
		} catch (error) {
			console.error('Gen1 command execution error:', error);
			return {
				success: false,
				metadata: {
					error: error.message,
				},
			};
		}
	}

	/**
	 * Execute Gen2+ command via RPC
	 */
	private async executeGen2Command(
		device: Device,
		context: SceneExecutionContext,
		command: any,
		options: ActionExecutionOptions,
	): Promise<{ success: boolean; metadata?: any }> {
		// STUB: Gen2 RPC execution not yet implemented.
		// When the RPC service is integrated, this should use device.rpc() or equivalent
		// to send commands to Gen2+ devices. Gen1 devices use HTTP commands (see executeGen1Command).
		// Until then, Gen2 actions will report success but take no action.
		console.warn('[V2 SceneActionEngine] Gen2 command execution not yet implemented:', command);

		return {
			success: true,
			metadata: {
				note: 'Gen2 execution stubbed - will be implemented when RPC service is integrated',
			},
		};
	}

	/**
	 * Parse Gen1 'set' parameter from endpoint path
	 * @param endpoint - Endpoint path like '/device/relay/control'
	 * @returns Set type: 'relay', 'light', 'roller', etc.
	 */
	private parseGen1SetFromEndpoint(endpoint: string): string {
		const match = endpoint.match(/\/device\/(\w+)\//);
		return match ? match[1] : 'relay';
	}

	private convertLegacyAction(
		legacyAction: LegacySceneAction,
	): { name: string; parameters: Record<string, ParameterValue> } | null {
		// Map legacy action names to V2 action names
		const actionNameMap: Record<string, string> = {
			turn: 'turn',
			toggle: 'toggle',
			brightness: 'brightness',
			color: 'color',
			temp: 'temperature',
			state: 'turn',
		};

		const v2ActionName = actionNameMap[legacyAction.name];

		if (!v2ActionName) {
			return null;
		}

		// Convert legacy parameters to V2 format
		const parameters: Record<string, ParameterValue> = {
			value: legacyAction.value,
		};

		// Add legacy-specific parameters
		if (legacyAction.index !== undefined) {
			parameters.channel = legacyAction.index;
		}

		if (legacyAction.slot !== undefined) {
			parameters.slot = legacyAction.slot;
		}

		return {
			name: v2ActionName,
			parameters,
		};
	}

	private validateParameterType(
		expectedType: string,
		value: ParameterValue,
	): { success: boolean; error?: string } {
		switch (expectedType) {
			case 'boolean':
				if (typeof value !== 'boolean') {
					return { success: false, error: 'Expected boolean value' };
				}
				break;

			case 'number':
				if (typeof value !== 'number' || isNaN(value)) {
					return { success: false, error: 'Expected numeric value' };
				}
				break;

			case 'string':
				if (typeof value !== 'string') {
					return { success: false, error: 'Expected string value' };
				}
				break;

			case 'enum':
				// Enum validation handled in constraints
				break;

			default:
				return { success: false, error: `Unknown parameter type: ${expectedType}` };
		}

		return { success: true };
	}

	private validateParameterConstraints(
		paramDef: any,
		value: ParameterValue,
	): { success: boolean; error?: string } {
		// Range validation
		if (paramDef.range && typeof value === 'number') {
			if (paramDef.range.min !== undefined && value < paramDef.range.min) {
				return { success: false, error: `Value must be >= ${paramDef.range.min}` };
			}
			if (paramDef.range.max !== undefined && value > paramDef.range.max) {
				return { success: false, error: `Value must be <= ${paramDef.range.max}` };
			}
		}

		// Enum validation
		if (paramDef.enumValues && !paramDef.enumValues.includes(value)) {
			return { success: false, error: `Value must be one of: ${paramDef.enumValues.join(', ')}` };
		}

		// Custom validation
		if (paramDef.validation?.customValidator) {
			if (!paramDef.validation.customValidator(value)) {
				return { success: false, error: 'Value failed custom validation' };
			}
		}

		return { success: true };
	}

	private validateRule(
		rule: any,
		parameters: Record<string, ParameterValue>,
	): { success: boolean; error?: string } {
		// STUB: Complex validation rule processing not yet implemented.
		// Should handle parameter dependency rules, range validation, and cross-parameter constraints.
		return { success: true };
	}

	private replacePlaceholders(obj: any, replacements: Record<string, any>): any {
		if (typeof obj === 'string') {
			// Replace {{placeholder}} syntax
			return obj.replace(/\{\{(\w+)\}\}/g, (match, key) => {
				return replacements[key] !== undefined ? replacements[key] : match;
			});
		}

		if (Array.isArray(obj)) {
			return obj.map((item) => this.replacePlaceholders(item, replacements));
		}

		if (obj && typeof obj === 'object') {
			const replaced: any = {};
			for (const [key, value] of Object.entries(obj)) {
				replaced[key] = this.replacePlaceholders(value, replacements);
			}
			return replaced;
		}

		return obj;
	}
}

// Export singleton instance
export const sceneActionEngine = SceneActionEngine.getInstance();
